/* eslint-disable react/prop-types */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FilterTableFrame,
  AFPTable,
  EmptyState,
  AFPTableRowAction,
  Pagination,
  StatusBadge,
  useModal,
  Button,
} from '@gsa/afp-component-library';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import TableFilters from './make-model-approval-filters';
import { ModelMakeStatuses, getEntrySourceBadge } from '../constants';
import { OPERATIONS, SUBJECTS } from '../../../utilities/constants';
import ApproveRejectConfirmationModal from './make-model-approve-reject-modal';
import MakeModelApprovalModalWrapper from './edit-make-model-approval-request';
import SourceBadge from './source-badge';
import SourceBadgeKey from './source-badge-key';

const MakeModelTable = ({
  tableProps,
  paginationState,
  setPaginationState,
}) => {
  const ability = useAppAbility();
  const canUpdateMakeModel = ability.can(OPERATIONS.Update, SUBJECTS.makeModel);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMakeModel, setSelectedMakeModel] = useState({});
  const [action, setAction] = useState(null);

  const {
    isOpen: isMakeModelOpen,
    openModal: openMakeModelModal,
    closeModal: closeMakeModelModal,
  } = useModal();

  const getActions = (row) => {
    const {
      original: { fleetApprovalStatus },
    } = row;
    const actions = [];

    // show approve only when fleetApprovalStatus is different from Approved
    if (fleetApprovalStatus !== ModelMakeStatuses.Approved.value) {
      actions.push({
        icon: 'check',
        label: 'Approve',
      });
    }
    // show reject only when fleetApprovalStatus is different from Rejected
    if (fleetApprovalStatus !== ModelMakeStatuses.Rejected.value) {
      actions.push({
        icon: 'close',
        label: 'Reject',
      });
    }
    if (fleetApprovalStatus !== ModelMakeStatuses.Archived.value) {
      actions.push({
        icon: 'edit',
        label: 'Edit',
      });
    }
    return actions;
  };

  const handleSelectedAction = (evt, row) => {
    setSelectedMakeModel(row.original);
    if (evt === 'Approve' || evt === 'Reject') {
      setAction(evt);
      setIsOpen(true);
    }
    if (evt === 'Edit') {
      openMakeModelModal();
    }
  };

  const closeMakeModelApprovalModel = () => {
    setIsOpen(false);
  };

  // Codes can be UIDs; when so, show only the first 8 characters.
  const formatCode = (code) =>
    code && code.length > 10 ? code.substring(0, 8) : code;

  // Renders a make/model name with its code and entry-source pill below it.
  const renderNameWithCode = (name, code, entrySource) => {
    const badge = getEntrySourceBadge(entrySource);
    return (
      <div>
        <div>{name}</div>
        <div className="text-base display-flex flex-align-center flex-wrap">
          <span>{formatCode(code)}</span>
          <SourceBadge badge={badge} className="margin-left-1" />
        </div>
      </div>
    );
  };

  const useColumns = () => {
    const columns = useMemo(
      () => [
        {
          Header: 'Make',
          accessor: 'bodyMake.makeName',
          Cell: ({
            row: {
              original: { bodyMake },
            },
          }) =>
            renderNameWithCode(
              bodyMake?.makeName,
              bodyMake?.nhtsaMakeCode || bodyMake?.makeCode,
              bodyMake?.entrySource,
            ),
        },
        {
          Header: 'Model',
          accessor: 'bodyModel.modelName',
          Cell: ({
            row: {
              original: { bodyModel },
            },
          }) =>
            renderNameWithCode(
              bodyModel?.modelName,
              bodyModel?.nhtsaModelCode || bodyModel?.modelCode,
              bodyModel?.entrySource,
            ),
        },
        {
          Header: 'Chassis make',
          accessor: 'chassisMake.makeName',
          Cell: ({
            row: {
              original: { chassisMake },
            },
          }) =>
            renderNameWithCode(
              chassisMake?.makeName,
              chassisMake?.nhtsaMakeCode || chassisMake?.makeCode,
              chassisMake?.entrySource,
            ),
        },
        {
          Header: 'Chassis model',
          accessor: 'chassisModel.modelName',
          Cell: ({
            row: {
              original: { chassisModel },
            },
          }) =>
            renderNameWithCode(
              chassisModel?.modelName,
              chassisModel?.nhtsaModelCode || chassisModel?.modelCode,
              chassisModel?.entrySource,
            ),
        },
        {
          Header: 'Fleet status',
          accessor: 'fleetApprovalStatus',
          sortable: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ value }) => {
            let variant;
            switch (value) {
              case ModelMakeStatuses.Approved.value:
                variant = 'Ready-Gray';
                break;
              case ModelMakeStatuses.PendingReview.value:
                variant = 'Warning-Gray';
                break;
              case ModelMakeStatuses.Rejected.value:
                variant = 'Urgent-Gray';
                break;
              case ModelMakeStatuses.Archived.value:
                variant = 'Inactive-Gray';
                break;
              default:
                variant = 'Inactive-Gray';
            }

            return (
              variant && (
                <StatusBadge variant={variant}>
                  {ModelMakeStatuses[value]?.label}
                </StatusBadge>
              )
            );
          },
        },
        {
          Header: 'Source',
          accessor: 'entrySource',
          sortable: false,
          Cell: ({
            row: {
              original: { entrySource },
            },
          }) => getEntrySourceBadge(entrySource)?.fullLabel || '',
        },
        {
          Header: 'Actions',
          sortable: false,
          minWidth: 30,
          width: 150,
          maxWidth: 400,
          Cell: (props) => {
            const actions = canUpdateMakeModel ? getActions(props.row) : [];
            return actions.length ? (
              <AFPTableRowAction
                {...props}
                actions={actions}
                onSelectAction={(evt) => handleSelectedAction(evt, props.row)}
              />
            ) : null;
          },
        },
      ],
      [canUpdateMakeModel],
    );

    return columns;
  };
  const columns = useColumns();

  const isNoData = !tableProps?.data?.length;

  return (
    <>
      <AFPTable data={tableProps?.data} columns={columns} {...tableProps} />
      {tableProps?.data.length > 0 && (
        <Pagination
          itemsPerPageOptions={[50, 100, 150, 200]}
          variant="advanced"
          onPageChange={(currentPage, itemsPerPage) => {
            const newState = {
              ...paginationState,
              currentPage,
              offset: (currentPage - 1) * itemsPerPage,
              limit: itemsPerPage,
            };
            setPaginationState(newState);
          }}
          itemsPerPage={paginationState.limit}
          currentPage={paginationState.currentPage}
          itemsCount={tableProps.count}
        />
      )}
      {isNoData && (
        <div className="text-center margin-top-neg-2 height-full">
          <EmptyState
            hasBackground
            containerStyles="padding-y-6 height-full"
            topText={<p>No data is available.</p>}
          />
        </div>
      )}

      {/* Modals */}

      {/* approval/rejection modal */}
      <ApproveRejectConfirmationModal
        isOpen={isOpen}
        data={selectedMakeModel}
        action={action}
        onClose={closeMakeModelApprovalModel}
        refetchData={tableProps?.refetchMakeModelApproval}
      />

      {/* edit approval request modal */}
      <MakeModelApprovalModalWrapper
        isOpen={isMakeModelOpen}
        onClose={closeMakeModelModal}
        data={selectedMakeModel}
        refetchData={tableProps?.refetchMakeModelApproval}
      />
    </>
  );
};

MakeModelTable.propTypes = {
  tableProps: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
  }).isRequired,
  paginationState: PropTypes.shape({
    currentPage: PropTypes.number,
    offset: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  setPaginationState: PropTypes.func.isRequired,
};

export default FilterTableFrame(
  null,
  SourceBadgeKey,
  TableFilters,
  MakeModelTable,
);
